import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { confirmEnrollment, disableTotp, startEnrollment } from '../totp.api';

export type EnrollmentState = { phase: 'idle' } | { phase: 'secret'; secret: string; qrCodeDataUrl: string } | { phase: 'recovery-codes'; codes: string[] };

/**
 * The enrolment wizard: idle → secret + QR shown → a code is entered → recovery
 * codes shown exactly once (unless email codes already issued a set, which still
 * stands) → back to idle, enabled. `disable` is separate — it doesn't pass
 * through the wizard at all.
 */
export function useTotpEnrollment() {
  const { state, setUser } = useCurrentUser();
  const toast = useToast();
  const [enrollment, setEnrollment] = useState<EnrollmentState>({ phase: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const user = state.status === 'authenticated' ? state.user : undefined;

  async function start(password?: string) {
    setError(null);
    setSubmitting(true);
    try {
      const { secret, qrCodeDataUrl } = await startEnrollment(password);
      setEnrollment({ phase: 'secret', secret, qrCodeDataUrl });
    } catch {
      setError(password ? 'Incorrect password.' : 'Could not start enrolment — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(code: string) {
    setError(null);
    setSubmitting(true);
    try {
      const { recoveryCodes } = await confirmEnrollment(code);
      if (recoveryCodes.length === 0) finish();
      else setEnrollment({ phase: 'recovery-codes', codes: recoveryCodes });
    } catch {
      setError('Incorrect code. Check your authenticator app and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /** Called once the user has confirmed they saved the recovery codes — closes the wizard. */
  function finish() {
    if (user) setUser({ ...user, totpEnabled: true });
    setEnrollment({ phase: 'idle' });
    toast.success('Authenticator app turned on');
  }

  async function disable(password: string) {
    setError(null);
    setSubmitting(true);
    try {
      await disableTotp(password);
      if (user) setUser({ ...user, totpEnabled: false });
      toast.success('Authenticator app turned off');
      return true;
    } catch {
      setError('Incorrect password.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  function cancel() {
    setEnrollment({ phase: 'idle' });
    setError(null);
  }

  return { enrollment, error, submitting, start, verify, finish, disable, cancel };
}
